import {
  organisationSchema,
  userProfileDataSchema,
  type Enums,
  type Organisation,
  type ParsedUserProfile,
  type RealtorUserProfile,
  type Tables,
} from '@aikoa/common'
import { supabase } from '~/lib/supabase'

type RawUserProfile = Tables<'user_profile'> & {
  user_roles: {
    role: Enums<'user_role'>
  }[]
}

async function fetchUserOrganisation(userId: string): Promise<Organisation | null> {
  const { data, error } = await supabase.rpc('get_user_organisation', {
    p_user_id: userId,
  })

  if (error || !data || data.length === 0) {
    return null
  }

  const rawOrg = data[0]
  const parseResult = organisationSchema.safeParse(rawOrg)

  if (!parseResult.success) {
    return null
  }

  return parseResult.data
}

export function parseUserProfile(
  data: RawUserProfile,
  organisation: Organisation | null = null
): ParsedUserProfile {
  const parsedData = userProfileDataSchema.parse(data.data)

  return {
    ...data,
    data: parsedData,
    roles: data.user_roles.map((role) => role.role),
    organisation,
  } satisfies ParsedUserProfile
}

export async function getCurrentUserProfile(): Promise<RealtorUserProfile | null> {
  const authResult = await supabase.auth.getUser()
  const authUser = authResult?.data?.user

  if (!authUser?.id) {
    return null
  }

  const { data, error } = await supabase
    .from('user_profile')
    .select('*, user_roles(role)')
    .eq('user_id', authUser.id)
    .maybeSingle()

  if (error || !data) {
    return null
  }

  const organisation = await fetchUserOrganisation(authUser.id)
  return {
    ...parseUserProfile(data, organisation),
    phone: authUser.phone ?? null,
    email: authUser.email ?? null,
  }
}
